import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../services/api'

interface Admin {
  id: string
  email: string
  is_primary?: boolean
  first_login?: boolean
  created_at: string
}

interface AuthContextType {
  admin: Admin | null
  csrfToken: string | null
  login: (email: string, password: string) => Promise<{ requires2FA: boolean; tempToken?: string; maskedEmail?: string } | void>
  verifyOTP: (tempToken: string, otpCode: string) => Promise<void>
  resendOTP: (tempToken: string) => Promise<{ maskedEmail?: string }>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null)
  const [csrfToken, setCsrfToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/auth/me')
      setAdmin(response.data)
      
      // Get CSRF token
      const csrfResponse = await api.get('/auth/csrf-token')
      setCsrfToken(csrfResponse.data.csrf_token)
    } catch (error) {
      // User is not authenticated
      setAdmin(null)
      setCsrfToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password })
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return {
          requires2FA: true,
          tempToken: response.data.temp_token,
          maskedEmail: response.data.masked_email
        }
      }
      
      // No 2FA required, check auth status
      await checkAuthStatus()
    } catch (error) {
      throw error
    }
  }

  const verifyOTP = async (tempToken: string, otpCode: string) => {
    try {
      await api.post('/auth/2fa/verify-email-otp', {
        temp_token: tempToken,
        otp_code: otpCode
      })
      await checkAuthStatus()
    } catch (error) {
      throw error
    }
  }

  const resendOTP = async (tempToken: string) => {
    try {
      const response = await api.post('/auth/2fa/resend-otp', {
        temp_token: tempToken
      })
      return {
        maskedEmail: response.data.masked_email
      }
    } catch (error) {
      throw error
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      setAdmin(null)
      setCsrfToken(null)
    }
  }

  return (
    <AuthContext.Provider value={{ admin, csrfToken, login, verifyOTP, resendOTP, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
